import React, { useContext, useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useMediaQuery } from 'react-responsive';
import { useLocation } from 'react-router-dom';
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';
import MenuIcon from '@mui/icons-material/Menu';
import ListAltIcon from '@mui/icons-material/ListAlt';
import AssignmentIcon from '@mui/icons-material/Assignment';
import DocumentScannerIcon from '@mui/icons-material/DocumentScanner';
import GroupsIcon from '@mui/icons-material/Groups';
import StorageIcon from '@mui/icons-material/Storage';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import HomeIcon from '@mui/icons-material/Home';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import MoveToInboxIcon from '@mui/icons-material/MoveToInbox';

import {
  AppBar,
  Backdrop,
  Box,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Tooltip,
  Typography,
} from '@mui/material';

import { useNavigate } from 'react-router-dom';
import logoImage from '../../assets/logo.png';
import logoCollapsedImage from '../../assets/side_bar_logo_close.svg';
import { sideBarWidth, spHeaderHeight } from '../../../constants/location';
import { gray20, white, white_overlay30, navy50 } from '../../../constants/colors';
import CustomSnackbar from './../form/CustomSnackbar';
import { doc, onSnapshot } from 'firebase/firestore';
import { FirebaseError } from '@firebase/util';
import { AuthContext } from '../../../contexts/auth';
import { usePatternList } from '../../../hooks/usePatternList';
import { useCompanyId } from '../../../hooks/useCompanyId';
import { useEmailMenuVisibility } from '../../../hooks/useEmailMenuVisibility';
import { db } from '../../../api/firebase/config';
import {
  companyPlan,
  isProGeminiModel,
  patternStatus,
  shareScope,
  zIndexLayer,
} from '../../../constants/enum';
import { getPlanLabel, getPlanLimits } from '../../../constants/plans';
import {
  OCR_USAGE_MONTHLY_LABEL,
  OCR_USAGE_MONTHLY_USAGE_LABEL,
  OCR_USAGE_CREDIT_LABEL,
  PATTERN_MODEL_SELECTABLE_TOOLTIP,
} from '../../../constants/labels';
import { buildOcrUsageDisplay } from '../../../utils/ocrUsageDisplay';

type Props = {
  pcIsNone?: boolean;
  collapsed?: boolean;
  onToggle?: () => void;
};

const SideBar = React.forwardRef((props: Props, _ref): React.ReactElement => {
  const { pcIsNone, collapsed = false, onToggle } = props;
  const { user } = useContext(AuthContext);
  const { companyId: effectiveCompanyId, isProxyMode } = useCompanyId();
  const [companyInfo, setCompanyInfo] = useState<{
    plan: number;
    ocrMonthlyBudget: number;
    currentMonthOcrCount: number;
    currentMonthOcrCost: number;
    ocrCreditBalance: number;
    enableMaster: boolean;
    enableDictionary: boolean;
  } | null>(null);

  // OCR利用状況の表示文言（#3146）。/company 画面と同じユーティリティで生成する
  const ocrUsageDisplay = companyInfo !== null ? buildOcrUsageDisplay(companyInfo) : null;
  const isNegativeCredit = ocrUsageDisplay?.isNegativeCredit ?? false;
  const planBoxColor = isNegativeCredit ? '#DBBB79' : gray20;

  const styles = {
    button: {
      '&.Mui-disabled': {
        '.MuiListItemText-root': {
          '.MuiTypography-root': {
            fontWeight: '700',
          },
        },
        opacity: 1,
      },
      '&:hover': {
        backgroundColor: white_overlay30,
        borderRadius: '8px',
      },
      columnGap: collapsed ? '0px' : '8px',
      height: '56px',
      borderRadius: '8px',
      padding: collapsed ? '16px 12px' : '16px 16px',
      justifyContent: collapsed ? 'center' : 'flex-start',
      '@media screen and (max-width:960px)': {
        padding: '16px',
        justifyContent: 'flex-start',
      },
    },
    buttonSelected: {
      backgroundColor: white_overlay30,
      bordeRadius: '8px',
    },
    buttonText: {
      '.MuiTypography-root': {
        fontSize: '16px',
      },
      color: '#FFF',
    },
    hamburgerMenu: {
      padding: '12px',
      color: '#fff',
      marginLeft: 'auto',
    },
    header: {
      bgcolor: navy50,
      width: '100%',
      top: isProxyMode ? '48px' : 0,
    },
    headerTitle: {
      flexGrow: 1,
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      minHeight: { xs: spHeaderHeight },
      padding: '0 12px',
    },
    headerWrapper: {
      flexGrow: 1,
      height: spHeaderHeight,
      position: 'sticky',
      top: 0,
    },
    icon: {
      color: '#FFF',
      width: '24px',
    },
    itemIcon: {
      minWidth: '24px',
    },
    link: {
      textDecoration: 'none',
    },
    list: {
      padding: 0,
      width: collapsed ? '48px' : '224px',
      margin: '0 auto',
    },
    listItem: {
      padding: 0,
    },
    logoPc: {
      cursor: 'pointer',
      width: '80px',
    },
    logoSp: {
      cursor: 'pointer',
      width: '80px',
    },
    bottomButtons: {
      '@media screen and (max-width:960px)': {
        flexShrink: 0,
      },
      width: collapsed ? '48px' : '224px',
      margin: '0 auto',
    },
    sideBarWrapper: {
      '@media screen and (max-width:960px)': {
        height: isProxyMode ? 'calc(100vh - 48px)' : '100vh',
        maxHeight: isProxyMode ? 'calc(100vh - 48px)' : '100vh',
        ml: 'auto',
        overflow: 'hidden',
        position: 'fixed',
        right: 0,
        // PC 側（1200）と揃える。OCR結果画面は共通レイアウトを介さず SideBar を直接置くため、
        // ラッパーの zIndex（sideBarWrapper）が付かず、画面側の固定フッターに負けて隠れてしまう。
        zIndex: zIndexLayer.sideBar,
        top: isProxyMode ? '48px' : 0,
        display: 'flex',
        flexDirection: 'column',
      },
      '@media screen and (min-width: 960px)': {
        display: pcIsNone ? 'none' : 'flex',
        width: collapsed ? '80px' : sideBarWidth,
      },
      backgroundColor: navy50,
      height: isProxyMode ? 'calc(100vh - 60px)' : '100vh',
      left: 0,
      position: 'sticky',
      overflowY: 'hidden',
      top: isProxyMode ? '60px' : 0,
      width: sideBarWidth,
      flexDirection: 'column',
      justifyContent: 'space-between',
      transition: 'width 0.3s ease',
      zIndex: zIndexLayer.sideBar,
    },
    navigationDrawer: {
      '@media screen and (max-width:960px)': {
        flex: 1,
        overflowY: 'auto',
        mt: '16px',
        mb: '16px',
      },
      flex: 1,
      overflowY: 'auto',
      overflowX: 'hidden',
      webkitOverflowScrolling: 'touch',
      minHeight: 0,
      marginTop: '26px',
      paddingBottom: '32px',
      // スクロールバーをサイドバーと同色に
      scrollbarWidth: 'thin',
      scrollbarColor: `rgba(255, 255, 255, 0.35) ${navy50}`,
      '&::-webkit-scrollbar': {
        width: '6px',
      },
      '&::-webkit-scrollbar-track': {
        backgroundColor: navy50,
      },
      '&::-webkit-scrollbar-thumb': {
        backgroundColor: 'rgba(255, 255, 255, 0.35)',
        borderRadius: '3px',
      },
      '&::-webkit-scrollbar-thumb:hover': {
        backgroundColor: 'rgba(255, 255, 255, 0.55)',
      },
    },
    topTitleWrapper: {
      '@media screen and (max-width:960px)': {
        flexShrink: 0,
        padding: '16px',
      },
      padding: collapsed ? '32px 0px 0px 0px' : '32px 16px 0px 24px',
      textAlign: collapsed ? 'center' : 'left',
      display: 'flex',
      alignItems: 'flex-start',
      justifyContent: collapsed ? 'center' : 'flex-start',
      flexShrink: 0,
    },
    title: {
      fontSize: '12px',
      color: 'rgba(255, 255, 255, 0.87)',
    },
    freePlanBox: {
      '@media screen and (max-width:960px)': {
        padding: '12px',
        flexShrink: 0,
      },
      color: '#FFF',
      padding: '12px',
      cursor: 'pointer',
      '&:hover': {
        backgroundColor: 'rgba(255, 255, 255, 0.05)',
      },
      borderRadius: '4px',
      margin: '0 12px 88px 12px',
      border: `1px solid ${planBoxColor}`,
      flexShrink: 0,
    },
    freePlanContent: {
      display: 'flex',
      flexDirection: 'column',
      gap: '4px',
    },
    freePlanTitle: {
      fontSize: '14px',
      fontWeight: 400,
      color: planBoxColor,
      textAlign: 'left',
    },
    freePlanRow: {
      display: 'flex',
      alignItems: 'center',
      fontSize: '11px',
      color: planBoxColor,
      gap: '4px',
    },
    freePlanLabel: {
      fontSize: '11px',
      color: planBoxColor,
      flex: '0 0 auto',
      whiteSpace: 'nowrap',
      textAlign: 'left',
    },
    freePlanValue: {
      fontSize: '11px',
      color: planBoxColor,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      flex: '1 1 auto',
      minWidth: 0,
    },
    freePlanValueText: {
      fontSize: '11px',
      color: planBoxColor,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },
    freePlanCreditValue: {
      fontSize: '11px',
      color: planBoxColor,
    },
    freePlanCreditRow: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      fontSize: '11px',
      color: gray20,
    },
    toggleButton: {
      position: 'fixed',
      left: collapsed ? '60px' : '220px', // サイドバーの外に半分出るように配置
      bottom: '24px',
      zIndex: 1001, // フッター（1000）より上に表示
      backgroundColor: navy50,
      border: '2px solid rgba(255, 255, 255, 0.1)',
      borderRadius: '8px',
      padding: '8px',
      minWidth: '40px',
      height: '40px',
      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.4), inset 0 1px 0 rgba(255, 255, 255, 0.1)',
      '&:hover': {
        backgroundColor: '#2c2c2c',
        boxShadow: '0 6px 16px rgba(0, 0, 0, 0.5), inset 0 1px 0 rgba(255, 255, 255, 0.15)',
        transform: 'translateY(-1px)',
      },
      transition: 'all 0.2s ease',
      '@media screen and (max-width:960px)': {
        display: 'none',
      },
    },
  };

  const sideBarText = {
    home: 'ホーム',
    pattern: '読取パターン一覧',
    freeOcr: 'フリーOCR',
    master: 'マスタ照合管理',
    dictionary: '辞書データ管理',
    inbox: '受信トレイ',
  };

  // メール取り込み（#2965）: 有料オプション。機能有効（＋開始日到達）の企業のみ受信トレイを表示
  const showEmailMenu = useEmailMenuVisibility();

  const isMobile: boolean = useMediaQuery({ query: '(max-width: 960px)' });
  const [menuOpen, setMenuOpen] = React.useState<boolean>(false);
  const [errorText, setErrorText] = useState<string>('');
  const [snackbarOpen, setSnackbarOpen] = useState<boolean>(false);

  const homeLink = '/home';
  const patternLink = '/pattern';
  const freeOcrLink = '/ai-ocr/free';

  // メインのボタンリスト
  // 注: API ドキュメントは設定系・開発者向け機能のため右上アカウントメニューから辿る。
  //     API設定（キー管理）はマスタ/辞書と並べてサイドバーにも配置する（#api-webhook-settings）。
  const buttonList = React.useMemo(() => {
    const items: Array<{ icon: JSX.Element; link: string; text: string }> = [
      {
        icon: <HomeIcon sx={styles.icon} />,
        link: homeLink,
        text: sideBarText.home,
      },
      {
        icon: <ListAltIcon sx={styles.icon} />,
        link: patternLink,
        text: sideBarText.pattern,
      },
    ];
    return items;
  }, []);

  const navigate = useNavigate();
  const location = useLocation();

  // 読取パターン一覧の取得
  const { patterns, refresh: refreshPatterns } = usePatternList();

  // パターンリスト更新イベントのリスナー
  useEffect(() => {
    const handlePatternListUpdate = (): void => {
      refreshPatterns();
    };

    window.addEventListener('pattern-list-updated', handlePatternListUpdate);

    return (): void => {
      window.removeEventListener('pattern-list-updated', handlePatternListUpdate);
    };
  }, [refreshPatterns]);

  // 会社情報のリアルタイム監視（フリープラン情報用）
  // 代理モード時は対象企業の情報を表示
  useEffect(() => {
    const companyIdToUse = effectiveCompanyId || user?.company_id;
    if (!companyIdToUse) return;

    const companyDocRef = doc(db, 'companies', companyIdToUse);
    const unsubscribe = onSnapshot(
      companyDocRef,
      snapshot => {
        if (snapshot.exists()) {
          const company = snapshot.data();
          const planCode = company.plan ?? companyPlan.free;
          const planLimits = getPlanLimits(planCode);
          setCompanyInfo({
            plan: planCode,
            ocrMonthlyBudget: company.ocr_monthly_budget ?? planLimits?.ocrMonthlyBudget ?? 0,
            currentMonthOcrCount: company.current_month_ocr_count ?? 0,
            currentMonthOcrCost: company.current_month_ocr_cost ?? 0,
            ocrCreditBalance: company.ocr_credit_balance ?? 0,
            enableMaster: (company.enable_master as boolean) ?? false,
            enableDictionary: (company.enable_dictionary as boolean) ?? false,
          });
        }
      },
      error => {
        // ログアウト直後にリスナーが permission-denied を受け取るのは正常な動作
        if (error instanceof FirebaseError && error.code === 'permission-denied') {
          return;
        }
        setErrorText('会社情報の取得に失敗しました');
        setSnackbarOpen(true);
      },
    );

    return (): void => {
      unsubscribe();
    };
  }, [effectiveCompanyId, user?.company_id]);

  return (
    <>
      {/* SPヘッダー。メニュー展開中はサイドバー・Backdrop より下に置き、
          ヘッダーが暗転する見た目を保つ（zIndexLayer 参照） */}
      {isMobile && (
        <Box
          sx={{
            ...styles.headerWrapper,
            zIndex: menuOpen ? zIndexLayer.spHeaderBehindMenu : zIndexLayer.spHeader,
          }}
        >
          <AppBar position="fixed" sx={styles.header} elevation={0}>
            <Toolbar sx={styles.headerTitle}>
              <Box
                component="img"
                src={logoImage}
                alt="Company Logo"
                sx={{
                  width: 180,
                }}
              />
              <IconButton
                edge="end"
                sx={styles.hamburgerMenu}
                onClick={(): void => setMenuOpen(!menuOpen)}
              >
                {!menuOpen ? <MenuIcon /> : <CloseOutlinedIcon />}
              </IconButton>
            </Toolbar>
          </AppBar>
        </Box>
      )}

      {/* サイドバーの直下。画面側の固定フッターより上に出す必要がある */}
      <Backdrop
        sx={{
          color: '#fff',
          zIndex: zIndexLayer.backdrop,
          // 非表示のときはクリックを受け取らない（#3259）。
          // トランジションが完了せず visibility: visible のまま残ると、
          // 透明な状態で画面を覆い、操作を奪ってしまうため
          pointerEvents: menuOpen ? 'auto' : 'none',
        }}
        open={menuOpen}
        onClick={(): void => setMenuOpen(false)}
      />

      {/* サイドバー ハンバーガーメニュー */}
      {(!isMobile || (isMobile && menuOpen)) && (
        <>
          <Box sx={styles.sideBarWrapper}>
            <Box sx={styles.topTitleWrapper}>
              <Box
                component="img"
                src={collapsed ? logoCollapsedImage : logoImage}
                alt="Company Logo"
                sx={{
                  width: collapsed ? 'auto' : 180,
                  height: 36,
                }}
              />
            </Box>

            {/* ナビゲーションエリア */}
            <Box sx={styles.navigationDrawer}>
              <List sx={styles.list}>
                {buttonList.map(button => {
                  // /pattern/:id の編集ページか判定（/pattern/add や /pattern/:id/accuracy は除外）
                  const patternEditMatch = location.pathname.match(/^\/pattern\/([^/]+)$/);
                  const editingPatternId =
                    patternEditMatch && patternEditMatch[1] !== 'add' ? patternEditMatch[1] : null;
                  const isPatternManagement =
                    location.pathname === '/pattern' || location.pathname.startsWith('/pattern/');
                  const isPatternAiOcr = location.pathname.startsWith('/ai-ocr/pattern/');
                  let isSelected = false;

                  if (editingPatternId) {
                    // 特定パターン編集中はメインボタン（読取パターン一覧）は選択しない
                    isSelected = false;
                  } else if (isPatternManagement) {
                    isSelected = button.link === '/pattern';
                  } else if (isPatternAiOcr) {
                    // 読取パターン別AI OCRページでは、メインボタンは選択しない
                    isSelected = false;
                  } else {
                    isSelected =
                      location.pathname.includes(button.link) && !button.link.includes('/pattern');
                  }

                  const buttonContent = (
                    <ListItemButton
                      sx={{
                        ...styles.button,
                        ...(isSelected ? styles.buttonSelected : {}),
                      }}
                      key={button.text}
                      onClick={(): void => navigate(button.link)}
                    >
                      <ListItemIcon sx={styles.itemIcon}>{button.icon}</ListItemIcon>
                      {!collapsed && (
                        <ListItemText
                          primary={button.text}
                          sx={{ ...styles.buttonText, textAlign: 'left' }}
                        />
                      )}
                    </ListItemButton>
                  );

                  return (
                    <ListItem sx={styles.listItem} key={button.text}>
                      {collapsed ? (
                        <Tooltip title={button.text} placement="right" arrow>
                          {buttonContent}
                        </Tooltip>
                      ) : (
                        buttonContent
                      )}
                    </ListItem>
                  );
                })}

                {/* マスタ管理（enable_master が true の企業のみ表示） */}
                {companyInfo?.enableMaster && (
                  <ListItem sx={styles.listItem}>
                    {collapsed ? (
                      <Tooltip title={sideBarText.master} placement="right" arrow>
                        <ListItemButton
                          sx={{
                            ...styles.button,
                            ...(location.pathname === '/master' ? styles.buttonSelected : {}),
                          }}
                          onClick={(): void => navigate('/master')}
                        >
                          <ListItemIcon sx={styles.itemIcon}>
                            <StorageIcon sx={styles.icon} />
                          </ListItemIcon>
                        </ListItemButton>
                      </Tooltip>
                    ) : (
                      <ListItemButton
                        sx={{
                          ...styles.button,
                          ...(location.pathname === '/master' ? styles.buttonSelected : {}),
                        }}
                        onClick={(): void => navigate('/master')}
                      >
                        <ListItemIcon sx={styles.itemIcon}>
                          <StorageIcon sx={styles.icon} />
                        </ListItemIcon>
                        <ListItemText
                          primary={sideBarText.master}
                          sx={{ ...styles.buttonText, textAlign: 'left' }}
                        />
                      </ListItemButton>
                    )}
                  </ListItem>
                )}

                {/* 辞書データ管理（enable_dictionary が true の企業のみ表示） */}
                {companyInfo?.enableDictionary && (
                  <ListItem sx={styles.listItem}>
                    {collapsed ? (
                      <Tooltip title={sideBarText.dictionary} placement="right" arrow>
                        <ListItemButton
                          sx={{
                            ...styles.button,
                            ...(location.pathname === '/dictionary' ? styles.buttonSelected : {}),
                          }}
                          onClick={(): void => navigate('/dictionary')}
                        >
                          <ListItemIcon sx={styles.itemIcon}>
                            <MenuBookIcon sx={styles.icon} />
                          </ListItemIcon>
                        </ListItemButton>
                      </Tooltip>
                    ) : (
                      <ListItemButton
                        sx={{
                          ...styles.button,
                          ...(location.pathname === '/dictionary' ? styles.buttonSelected : {}),
                        }}
                        onClick={(): void => navigate('/dictionary')}
                      >
                        <ListItemIcon sx={styles.itemIcon}>
                          <MenuBookIcon sx={styles.icon} />
                        </ListItemIcon>
                        <ListItemText
                          primary={sideBarText.dictionary}
                          sx={{ ...styles.buttonText, textAlign: 'left' }}
                        />
                      </ListItemButton>
                    )}
                  </ListItem>
                )}

                {/* 読取パターン別AI OCR */}
                {/* ローディング中でもデータがあれば表示（画面遷移時のちらつき防止） */}
                {patterns
                  .filter(pattern => pattern.status !== patternStatus.deleted)
                  .map(pattern => {
                    const patternPath = `/ai-ocr/pattern/${pattern.patternId}`;
                    // 特定パターン編集中（/pattern/:id）も該当パターンをハイライト（#2804）
                    const editingPatternMatch = location.pathname.match(/^\/pattern\/([^/]+)$/);
                    const editingPatternId =
                      editingPatternMatch && editingPatternMatch[1] !== 'add'
                        ? editingPatternMatch[1]
                        : null;
                    const isPatternSelected =
                      location.pathname === patternPath || editingPatternId === pattern.patternId;

                    const buttonContent = (
                      <ListItemButton
                        sx={{
                          ...styles.button,
                          ...(isPatternSelected ? styles.buttonSelected : {}),
                        }}
                        key={pattern.patternId}
                        onClick={(): void => navigate(patternPath)}
                      >
                        <ListItemIcon sx={styles.itemIcon}>
                          <AssignmentIcon sx={styles.icon} />
                        </ListItemIcon>
                        {!collapsed && (
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '6px',
                              overflow: 'hidden',
                            }}
                          >
                            <ListItemText
                              primary={pattern.pattern_name}
                              sx={{ ...styles.buttonText, textAlign: 'left', overflow: 'hidden' }}
                            />
                            {/* グループ限定パターンはグループアイコンを白抜きで表示（#2804） */}
                            {pattern.share_scope === shareScope.group && (
                              <Box
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  flexShrink: 0,
                                }}
                              >
                                <GroupsIcon sx={{ fontSize: '16px', color: white }} />
                              </Box>
                            )}
                            {isProGeminiModel(pattern.gemini_model) && (
                              <Tooltip title={PATTERN_MODEL_SELECTABLE_TOOLTIP} arrow>
                                <Box
                                  sx={{
                                    fontSize: '10px',
                                    padding: '2px 6px',
                                    backgroundColor: white,
                                    color: navy50,
                                    borderRadius: '4px',
                                    whiteSpace: 'nowrap',
                                    flexShrink: 0,
                                    fontWeight: 'bold',
                                  }}
                                >
                                  Pro
                                </Box>
                              </Tooltip>
                            )}
                          </Box>
                        )}
                      </ListItemButton>
                    );

                    return (
                      <ListItem sx={styles.listItem} key={pattern.patternId}>
                        {collapsed ? (
                          <Tooltip title={pattern.pattern_name} placement="right" arrow>
                            {buttonContent}
                          </Tooltip>
                        ) : (
                          buttonContent
                        )}
                      </ListItem>
                    );
                  })}

                {/* 受信トレイ（#2965）: メール取り込みの受信一覧。有料オプション有効時のみ表示 */}
                {showEmailMenu &&
                  ((): React.ReactElement => {
                    const inboxLink = '/company-settings/email-messages';
                    const isInboxSelected = location.pathname.startsWith(inboxLink);
                    const buttonContent = (
                      <ListItemButton
                        sx={{
                          ...styles.button,
                          ...(isInboxSelected ? styles.buttonSelected : {}),
                        }}
                        onClick={(): void => navigate(inboxLink)}
                      >
                        <ListItemIcon sx={styles.itemIcon}>
                          <MoveToInboxIcon sx={styles.icon} />
                        </ListItemIcon>
                        {!collapsed && (
                          <ListItemText primary={sideBarText.inbox} sx={styles.buttonText} />
                        )}
                      </ListItemButton>
                    );
                    return (
                      <ListItem sx={styles.listItem}>
                        {collapsed ? (
                          <Tooltip title={sideBarText.inbox} placement="right" arrow>
                            {buttonContent}
                          </Tooltip>
                        ) : (
                          buttonContent
                        )}
                      </ListItem>
                    );
                  })()}

                {/* フリーOCR */}
                {((): React.ReactElement => {
                  const isFreeOcrSelected = location.pathname.startsWith(freeOcrLink);
                  const buttonContent = (
                    <ListItemButton
                      sx={{
                        ...styles.button,
                        ...(isFreeOcrSelected ? styles.buttonSelected : {}),
                      }}
                      onClick={(): void => navigate(freeOcrLink)}
                    >
                      <ListItemIcon sx={styles.itemIcon}>
                        <DocumentScannerIcon sx={styles.icon} />
                      </ListItemIcon>
                      {!collapsed && (
                        <ListItemText primary={sideBarText.freeOcr} sx={styles.buttonText} />
                      )}
                    </ListItemButton>
                  );
                  return (
                    <ListItem sx={styles.listItem}>
                      {collapsed ? (
                        <Tooltip title={sideBarText.freeOcr} placement="right" arrow>
                          {buttonContent}
                        </Tooltip>
                      ) : (
                        buttonContent
                      )}
                    </ListItem>
                  );
                })()}
              </List>
            </Box>

            {/* プラン情報（フリープラン or OCRクレジットがマイナスの場合に表示、折りたたみ時は非表示） */}
            {!collapsed &&
              companyInfo &&
              ocrUsageDisplay &&
              (companyInfo.plan === companyPlan.free || isNegativeCredit) && (
                <Box sx={styles.freePlanBox} onClick={(): void => navigate('/company')}>
                  <Box sx={styles.freePlanContent}>
                    <Typography sx={styles.freePlanTitle}>
                      {getPlanLabel(companyInfo.plan)}
                    </Typography>
                    <Box sx={styles.freePlanRow}>
                      <Typography sx={styles.freePlanLabel}>{OCR_USAGE_MONTHLY_LABEL}</Typography>
                      <Box sx={styles.freePlanValue}>
                        <Typography sx={styles.freePlanValueText}>
                          {ocrUsageDisplay.monthlyOcrText}
                        </Typography>
                        <KeyboardArrowRightIcon sx={{ fontSize: '16px', marginLeft: '4px' }} />
                      </Box>
                    </Box>
                    <Box sx={styles.freePlanRow}>
                      <Typography sx={styles.freePlanLabel}>{OCR_USAGE_CREDIT_LABEL}</Typography>
                      <Box sx={styles.freePlanValue}>
                        <Typography sx={styles.freePlanCreditValue}>
                          {ocrUsageDisplay.creditBalanceText}
                        </Typography>
                        <Box sx={{ width: '16px', marginLeft: '4px' }} />
                      </Box>
                    </Box>
                    <Box sx={styles.freePlanRow}>
                      <Typography sx={styles.freePlanLabel}>
                        {OCR_USAGE_MONTHLY_USAGE_LABEL}
                      </Typography>
                      <Box sx={styles.freePlanValue}>
                        <Typography sx={styles.freePlanValueText}>
                          {ocrUsageDisplay.monthlyReadText}
                        </Typography>
                        <Box sx={{ width: '16px', marginLeft: '4px' }} />
                      </Box>
                    </Box>
                  </Box>
                </Box>
              )}
          </Box>
        </>
      )}

      {/* 開閉ボタン - Portalを使ってルートレベルにレンダリング */}
      {!isMobile &&
        onToggle &&
        createPortal(
          <IconButton sx={styles.toggleButton} onClick={onToggle}>
            {collapsed ? (
              <ChevronRightIcon sx={{ color: white, fontSize: '24px' }} />
            ) : (
              <ChevronLeftIcon sx={{ color: white, fontSize: '24px' }} />
            )}
          </IconButton>,
          document.body,
        )}

      <CustomSnackbar
        text={errorText}
        open={snackbarOpen}
        onClose={(): void => setSnackbarOpen(false)}
      />
    </>
  );
});

SideBar.displayName = 'SideBar';
export default SideBar;
